// Documentazione API — sezioni "CoreApiDocs". JSX estratto verbatim da DeveloperSettings.tsx.
import type { ReactElement } from 'react';
import { getPublicBaseUrl } from '@/lib/api';
import { ALL_AVAILABLE_SCOPES } from '@easyflow/shared';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-frontend/components/ui/table';

const availableScopes = ALL_AVAILABLE_SCOPES;
import { DocSection, Endpoint, CodeBlock } from './primitives';

export function renderCoreDocSection(section: string): ReactElement | null {
  switch (section) {
      case 'auth':
        return (
          <DocSection id="auth">
            <h3 className="text-lg font-semibold">Authentication & API Keys</h3>
            <p className="text-sm text-muted-foreground">All API v2 requests require an API key sent via the <code className="bg-muted px-1 rounded">X-API-Key</code> header.</p>
            <CodeBlock title="Example Request">{`curl -X GET ${getPublicBaseUrl()}/api/v2/voip/trunks \\
  -H "X-API-Key: sk_live_..." \\
  -H "Content-Type: application/json"`}</CodeBlock>
            <h4 className="text-sm font-semibold mt-4">API Key Format</h4>
            <p className="text-sm text-muted-foreground"><code className="bg-muted px-1 rounded">sk_live_{'<48 hex characters>'}</code></p>
            <h4 className="text-sm font-semibold mt-4">Available Scopes</h4>
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Scope</TableHead><TableHead>Description</TableHead></TableRow></TableHeader>
                <TableBody>
                  {availableScopes.map((s) => (
                    <TableRow key={s.id}><TableCell className="font-mono text-xs">{s.id}</TableCell><TableCell className="text-sm">{s.label}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </DocSection>
        );

      case 'stores':
        return (
          <DocSection id="stores">
            <h3 className="text-lg font-semibold">Stores API</h3>
            <p className="text-sm text-muted-foreground">
              Gestione degli store (sedi/punti vendita) del tenant.
              Base: <code className="bg-muted px-1 rounded">/api/v2/voip/stores</code>.
              Le rotte di lettura richiedono lo scope <code className="bg-muted px-1 rounded">stores:read</code>;
              il sync richiede <code className="bg-muted px-1 rounded">stores:sync</code>.
            </p>

            <h4 className="text-sm font-semibold mt-4">Read</h4>
            <Endpoint
              method="GET"
              path="/api/v2/voip/stores"
              desc="Lista degli store del tenant. Ordinata per name ASC. Nessun filtro né paginazione server-side: paginazione/filtri applicati lato client."
              scope="stores:read"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/stores/:store_id"
              desc="Recupera un singolo store per store_id custom (NON UUID interno). Restituisce 404 se non esiste."
              scope="stores:read"
            />

            <h4 className="text-sm font-semibold mt-4">Sync</h4>
            <Endpoint
              method="POST"
              path="/api/v2/voip/stores/sync"
              desc="Endpoint di sync. Implementazione attuale: ritorna lo snapshot corrente degli store (created/updated sempre 0; EDGVoIP non crea/modifica store, è child di W3 Suite). Body opzionale { force, store_ids[] } accettato ma non ancora applicato. La API key deve avere ENTRAMBI gli scope: stores:read (router-level) + stores:sync (route-level)."
              scope="stores:read + stores:sync"
            />

            <CodeBlock title="GET /api/v2/voip/stores — Response">{`{
  "success": true,
  "data": [
    {
      "id": "uuid",
      "store_id": "store-001",
      "name": "Main Store",
      "status": "active",
      "settings": {},
      "created_at": "2026-05-04T10:00:00Z",
      "updated_at": "2026-05-04T10:00:00Z"
    }
  ],
  "meta": { "total": 1 }
}`}</CodeBlock>

            <CodeBlock title="POST /api/v2/voip/stores/sync — Request">{`{
  "force": false,
  "store_ids": ["store-001", "store-002"]   // opzionale, restringe il sync
}`}</CodeBlock>

            <CodeBlock title="POST /api/v2/voip/stores/sync — Response">{`{
  "success": true,
  "synced": 2,
  "created": 0,
  "updated": 0,
  "errors": []
}`}</CodeBlock>
          </DocSection>
        );

      case 'trunks':
        return (
          <DocSection id="trunks">
            <h3 className="text-lg font-semibold">SIP Trunks API</h3>
            <p className="text-sm text-muted-foreground">
              Gestione trunk SIP del tenant. Base: <code className="bg-muted px-1 rounded">/api/v2/voip/trunks</code>.
              Lettura: <code className="bg-muted px-1 rounded">voip:read</code>; scrittura: <code className="bg-muted px-1 rounded">voip:write</code>;
              sync bidirezionale: <code className="bg-muted px-1 rounded">trunks:sync</code>.
              Lo stato registrazione è anche raggiungibile sotto <code className="bg-muted px-1 rounded">/api/v2/voip/status/trunks</code>.
            </p>

            <h4 className="text-sm font-semibold mt-4">Read</h4>
            <Endpoint
              method="GET"
              path="/api/v2/voip/trunks"
              desc="Lista trunk con stato SIP registration aggregato. La risposta contiene sip_registration_status (Registrato/Non registrato/Errore) e sip_registration_state."
              scope="voip:read"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/trunks/:external_id"
              desc="Recupera trunk per external_id."
              scope="voip:read"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/trunks/:external_id/status"
              desc="Stato registrazione SIP corrente del singolo trunk."
              scope="voip:read"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/status/trunks"
              desc="Variante sotto namespace status: aggrega lo stato corrente di tutti i trunk."
              scope="voip:read"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/status/trunks/:external_id"
              desc="Variante sotto namespace status: stato di un singolo trunk per external_id."
              scope="voip:read"
            />

            <h4 className="text-sm font-semibold mt-4">Write</h4>
            <Endpoint
              method="POST"
              path="/api/v2/voip/trunks"
              desc="Crea un nuovo trunk. La validazione di route esige solo: name, sip_config (oggetto), did_config (oggetto). Le sotto-chiavi di sip_config (host, username, password, transport) NON sono validate dal route ma sono richieste dal SIP gateway a runtime."
              scope="voip:write"
            />
            <Endpoint
              method="PUT"
              path="/api/v2/voip/trunks/:external_id"
              desc="Aggiorna trunk. Campi accettati: store_id, name, provider, sip_config, did_config, security, gdpr, codec_preferences. Nessun campo settings: passarlo non ha effetto."
              scope="voip:write"
            />
            <Endpoint
              method="DELETE"
              path="/api/v2/voip/trunks/:external_id"
              desc="Elimina trunk dal tenant e dalla configurazione FreeSWITCH."
              scope="voip:write"
            />
            <Endpoint
              method="POST"
              path="/api/v2/voip/trunks/:external_id/duplicate"
              desc="Clona trunk con sip_config, did_config, codec_preferences. Aggiunge suffisso (Copy)."
              scope="voip:write"
            />
            <Endpoint
              method="POST"
              path="/api/v2/voip/trunks/:external_id/test-connection"
              desc="Forza un sofia rescan/options ping via ESL e ritorna l'esito (state, response_code, response_time_ms)."
              scope="voip:write"
            />

            <h4 className="text-sm font-semibold mt-4">Sync</h4>
            <Endpoint
              method="POST"
              path="/api/v2/voip/trunks/sync"
              desc="Sync bidirezionale. Body vuoto → snapshot. Con trunks[] applica last-write-wins su updated_at; con force=true override locale. La API key deve avere ENTRAMBI gli scope: voip:read (router-level) + trunks:sync (route-level)."
              scope="voip:read + trunks:sync"
            />

            <CodeBlock title="POST /trunks — Create Request">{`{
  "external_id": "crm-trunk-1",
  "name": "Main Trunk",
  "store_id": "store-001",
  "sip_config": {
    "host": "sip.provider.com",
    "port": 5060,
    "username": "user",
    "password": "pass",
    "transport": "udp"          // udp | tcp | tls
  },
  "did_config": { "numbers": ["+1234567890"] },
  "codec_preferences": ["PCMU", "PCMA", "G729"],
  "settings": {}
}`}</CodeBlock>

            <CodeBlock title="GET /trunks/:external_id/status — Response">{`{
  "success": true,
  "data": {
    "id": "uuid",
    "name": "Main Trunk",
    "sip_registration_status": "Registrato",     // Registrato | Non registrato | Errore
    "sip_registration_state": "registered"
  }
}`}</CodeBlock>

            <CodeBlock title="POST /trunks/:external_id/test-connection — Response">{`# OK
{ "success": true,  "status": "connected",    "details": "Status: UP\\nReg: REGED\\n..." }

# Gateway censito ma non registrato
{ "success": false, "status": "disconnected", "details": "Status: DOWN\\n..." }

# FreeSWITCH/ESL non disponibile o gateway sconosciuto
{ "success": false, "error": "FreeSWITCH not connected" }
{ "success": false, "status": "unknown", "details": "No gateway found in FreeSWITCH" }`}</CodeBlock>

            <CodeBlock title="POST /trunks/sync — Request / Response">{`# Request
{
  "force": false,
  "trunks": [
    {
      "external_id": "crm-trunk-1",
      "name": "Main Trunk",
      "sip_config": { "host": "sip.provider.com", "username": "user", "password": "pass" },
      "updated_at": "2026-05-03T18:00:00Z"
    }
  ]
}

# Response
{
  "success": true,
  "synced": 1,
  "created": 0,
  "updated": 1,
  "errors": [],
  "trunks": [
    { "id": "uuid", "external_id": "crm-trunk-1", "action": "updated_remote_newer" }
  ]
}`}</CodeBlock>
          </DocSection>
        );

      case 'extensions':
        return (
          <DocSection id="extensions">
            <h3 className="text-lg font-semibold">Extensions API</h3>
            <p className="text-sm text-muted-foreground">
              Gestione SIP extensions del tenant. Base: <code className="bg-muted px-1 rounded">/api/v2/voip/extensions</code>.
              Tutte le rotte di lettura richiedono lo scope <code className="bg-muted px-1 rounded">voip:read</code>;
              le rotte di scrittura richiedono <code className="bg-muted px-1 rounded">extensions:write</code> e
              la sincronizzazione richiede <code className="bg-muted px-1 rounded">extensions:sync</code>.
              Gli endpoint di stato in tempo reale sono raggiungibili anche sotto
              <code className="bg-muted px-1 rounded ml-1">/api/v2/voip/status/extensions</code>.
            </p>

            <h4 className="text-sm font-semibold mt-4">Read</h4>
            <Endpoint
              method="GET"
              path="/api/v2/voip/extensions"
              desc="Lista delle extension del tenant. Query opzionale: store_id (id custom dello store o UUID interno). La risposta è ordinata per numero (extension::integer). Filtri server-side enabled / limit / offset NON sono attualmente implementati dal backend (vedi nota sotto): vanno applicati lato client."
              scope="voip:read"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/extensions/blf-list"
              desc="Lista compatta per Busy Lamp Field: extension, display_name, sip_uri, status (available/dnd/offline), dnd. Include provisioning_hint (Yealink, Polycom)."
              scope="voip:read"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/extensions/:external_id"
              desc="Recupera una singola extension per external_id."
              scope="voip:read"
            />

            <h4 className="text-sm font-semibold mt-4">Write</h4>
            <Endpoint
              method="POST"
              path="/api/v2/voip/extensions"
              desc="Crea una nuova extension. Campi obbligatori: extension, display_name. Opzionali: password, sip_domain, sip_password, store_id, type, settings. Se sip_domain non viene passato eredita tenant.slug; se sip_password non viene passato il server genera una password URL-safe a 24 caratteri."
              scope="extensions:write"
            />
            <Endpoint
              method="PUT"
              path="/api/v2/voip/extensions/:external_id"
              desc="Aggiorna i campi mutabili dell'extension (extension, display_name, password, sip_domain, sip_password, type, settings, store_id). Tutti opzionali: vengono aggiornati solo i campi presenti nel body."
              scope="extensions:write"
            />
            <Endpoint
              method="DELETE"
              path="/api/v2/voip/extensions/:external_id"
              desc="Elimina l'extension dal tenant e dalla configurazione FreeSWITCH."
              scope="extensions:write"
            />
            <Endpoint
              method="POST"
              path="/api/v2/voip/extensions/:id/duplicate"
              desc="Duplica un'extension esistente (id = UUID interno) clonando settings, type e password. Aggiunge suffisso _copy / (Copy)."
              scope="extensions:write"
            />
            <Endpoint
              method="POST"
              path="/api/v2/voip/extensions/:external_id/reset-password"
              desc="Ruota la password SIP. Genera una password URL-safe a 24 caratteri lato server, la propaga alla directory FreeSWITCH (best-effort) ed emette il webhook extension.updated con event_reason='password_rotated'. Il plaintext è restituito SOLO in questa response. Da preferire a PUT/POST con sip_password custom quando serve solo ruotare la credenziale senza toccare altri campi."
              scope="extensions:write"
            />

            <h4 className="text-sm font-semibold mt-4">Sync</h4>
            <Endpoint
              method="POST"
              path="/api/v2/voip/extensions/sync"
              desc="Sync bidirezionale con un sistema esterno. Body vuoto → ritorna lo snapshot corrente. Con extensions[] crea le mancanti e applica una strategia last-write-wins basata su updated_at; con force=true forza l'override locale."
              scope="extensions:sync"
            />

            <h4 className="text-sm font-semibold mt-4">Status (real-time)</h4>
            <Endpoint
              method="GET"
              path="/api/v2/voip/extensions/status/all"
              desc="Stato registrazione SIP di tutte le extension del tenant (is_registered, registration_ip, user_agent, last_seen)."
              scope="voip:read"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/status/extensions"
              desc="Variante sotto il namespace /api/v2/voip/status: aggrega lo stato corrente di tutte le extension. Stesso payload di /api/v2/voip/extensions/status/all."
              scope="voip:read"
            />
            <Endpoint
              method="GET"
              path="/api/v2/voip/status/extensions/:external_id"
              desc="Stato di una singola extension per external_id. Restituisce 404 se l'extension non esiste."
              scope="voip:read"
            />

            <h4 className="text-sm font-semibold mt-6">Esempi payload</h4>

            <CodeBlock title="GET /api/v2/voip/extensions — Query parameters">{`GET /api/v2/voip/extensions?store_id=store-001
X-API-Key: sk_live_...

# Parametri attualmente implementati dal backend:
#   store_id (opzionale) : id custom dello store o UUID interno.
#                          Se passato, vengono restituite solo le
#                          extension del tenant appartenenti a quello
#                          store. La risposta è ordinata per
#                          extension::integer.
#
# Parametri NON ancora supportati lato server (filtri/paginazione
# previsti come backend follow-up):
#   enabled  : bool   — filtro per stato (true → solo attive)
#   limit    : number — paginazione (default proposto 100, max 500)
#   offset   : number — paginazione
#
# Finché il backend non li espone, vanno applicati lato client sulla
# lista completa restituita. La forma futura della query string sarà:
#   GET /api/v2/voip/extensions?store_id=store-001&enabled=true&limit=50&offset=0`}</CodeBlock>

            <CodeBlock title="POST /extensions — Create Request">{`{
  "external_id": "crm-ext-1001",        // opzionale, default = id interno
  "extension": "1001",
  "display_name": "John Doe",
  "password": "securePass123",          // alias legacy di sip_password
  "sip_domain": "acme.sip.easyflowai.it",  // opzionale — default = tenant.slug
  "sip_password": "MyCustomPass!2026",  // opzionale — default = generata server-side (24 char URL-safe)
  "store_id": "store-001",
  "type": "user",                       // user | virtual | queue_member
  "settings": {
    "voicemail_enabled": true,
    "call_forwarding": { "enabled": false }
  }
}

# Quando passare sip_domain / sip_password:
# - sip_domain     → scenari multi-brand in cui la stessa tenant serve più
#                    domini SIP (es. brand1.sip.easyflowai.it / brand2.sip...).
#                    Se omesso, il backend eredita tenant.slug.
# - sip_password   → quando l'integrazione esterna (CRM, dialer) deve
#                    pre-impostare una credenziale nota lato suo. Se omesso,
#                    il server genera una password URL-safe a 24 caratteri
#                    (stesso algoritmo di /reset-password). Per la sola
#                    rotazione successiva preferire l'endpoint dedicato
#                    POST /:external_id/reset-password.`}</CodeBlock>

            <CodeBlock title="cURL — POST /extensions">{`curl -X POST ${getPublicBaseUrl()}/api/v2/voip/extensions \\
  -H "X-API-Key: sk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "external_id": "crm-ext-1001",
    "extension": "1001",
    "display_name": "John Doe",
    "store_id": "store-001",
    "sip_domain": "acme.sip.easyflowai.it",
    "sip_password": "MyCustomPass!2026"
  }'`}</CodeBlock>

            <CodeBlock title="POST /extensions — Create Response">{`{
  "success": true,
  "data": {
    "id": "uuid",
    "external_id": "crm-ext-1001",
    "store_id": "store-uuid",
    "extension": "1001",
    "display_name": "John Doe",
    "status": "active",
    "type": "user",
    "settings": { "voicemail_enabled": true },
    "created_at": "2025-11-30T10:00:00Z",
    "updated_at": "2025-11-30T10:00:00Z"
  }
}`}</CodeBlock>

            <CodeBlock title="PUT /extensions/:external_id — Update Request">{`{
  "display_name": "John D.",
  "sip_domain": "brand2.sip.easyflowai.it",  // opzionale — sovrascrive il dominio SIP corrente
  "sip_password": "RotatedPass!2026",     // opzionale — sovrascrive la password (preferire /reset-password per la sola rotazione)
  "settings": { "voicemail_enabled": false }
  // tutti i campi sono opzionali — vengono aggiornati solo quelli presenti
}`}</CodeBlock>

            <CodeBlock title="cURL — PUT /extensions/:external_id">{`curl -X PUT ${getPublicBaseUrl()}/api/v2/voip/extensions/crm-ext-1001 \\
  -H "X-API-Key: sk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "display_name": "John D.",
    "sip_domain": "brand2.sip.easyflowai.it"
  }'`}</CodeBlock>

            <CodeBlock title="POST /extensions/:external_id/reset-password — Response">{`{
  "success": true,
  "data": {
    "external_id": "crm-ext-1001",
    "extension": "1001",
    "sip_username": "1001@tenant-domain",
    "sip_password": "Aa1B2c3D4eF5g-_h6I7j8K9l",   // 24 char URL-safe, mostrato SOLO ora
    "rotated_at": "2026-05-04T09:12:34.567Z"
  },
  "message": "SIP password reset successfully"
}

# Effetti collaterali (best-effort, non bloccanti):
# - aggiornamento directory FreeSWITCH + reloadxml
# - webhook extension.updated con data.event_reason = "password_rotated"`}</CodeBlock>

            <CodeBlock title="POST /extensions/sync — Request">{`{
  "force": false,
  "extensions": [
    {
      "external_id": "crm-ext-1001",
      "extension": "1001",
      "display_name": "John Doe",
      "password": "tempPass",                  // alias legacy di sip_password
      "sip_domain": "acme.sip.easyflowai.it",     // opzionale — default = tenant.slug
      "sip_password": "MyCustomPass!2026",     // opzionale — default = generata server-side
      "store_id": "store-001",
      "type": "user",
      "settings": { "voicemail_enabled": true },
      "updated_at": "2026-05-03T18:00:00Z"
    }
  ]
}

# Strategia di merge:
# - se l'external_id non esiste → create
# - se esiste e force=true     → update sempre
# - altrimenti                 → update solo se incoming.updated_at > existing.updated_at
#                                (azione: updated_remote_newer | edgvoip_wins | unchanged)`}</CodeBlock>

            <CodeBlock title="POST /extensions/sync — Response">{`{
  "success": true,
  "synced": 3,
  "created": 1,
  "updated": 1,
  "errors": [
    { "external_id": "crm-ext-9999", "error": "Missing required fields: extension, display_name" }
  ],
  "extensions": [
    { "id": "uuid", "external_id": "crm-ext-1001", "extension": "1001", "action": "created" },
    { "id": "uuid", "external_id": "crm-ext-1002", "extension": "1002", "action": "updated_remote_newer" },
    { "id": "uuid", "external_id": "crm-ext-1003", "extension": "1003", "action": "edgvoip_wins" }
  ]
}`}</CodeBlock>

            <CodeBlock title="POST /extensions/:id/duplicate — Response">{`{
  "success": true,
  "data": {
    "id": "new-uuid",
    "external_id": "new-uuid",
    "extension": "1001_copy",
    "display_name": "John Doe (Copy)",
    "type": "user",
    "status": "active",
    "settings": { "voicemail_enabled": true }
  }
}`}</CodeBlock>

            <CodeBlock title="GET /extensions/blf-list — Response">{`{
  "success": true,
  "data": [
    {
      "extension": "1001",
      "display_name": "John Doe",
      "sip_uri": "1001@tenant.sip.easyflowai.it",
      "status": "available",   // available | dnd | offline
      "dnd": false
    }
  ],
  "meta": {
    "total": 1,
    "sip_domain": "tenant.sip.easyflowai.it",
    "timestamp": "2026-05-04T09:00:00Z"
  },
  "provisioning_hint": {
    "yealink":  { "line_key_type": 16, "line_key_value": "<extension>", "line_key_label": "<display_name>", "line_key_line": 1 },
    "polycom":  { "attendant_type": "blf", "attendant_value": "<extension>", "attendant_label": "<display_name>" }
  }
}`}</CodeBlock>

            <CodeBlock title="GET /extensions/status/all — Response">{`{
  "success": true,
  "data": [
    {
      "id": "uuid",
      "external_id": "crm-ext-1001",
      "extension": "1001",
      "display_name": "John Doe",
      "is_registered": true,
      "registration_ip": "82.55.12.34",
      "user_agent": "Yealink SIP-T46U 108.86.0.40",
      "last_seen": "2026-05-04T09:11:02Z"
    }
  ]
}`}</CodeBlock>

          </DocSection>
        );

    default:
      return null;
  }
}
